import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SerpMatchConfig } from "./types";

/**
 * AUT-496. The two numbers behind SERP Match's cutoff.
 *
 * They are on this tab because the ticket asks for them "alongside the other KPS settings", and
 * **not** because SERP Match feeds KPS — it does not in this version, which is why both aliases sit
 * in `RECALCULATION_EXCLUDED_ALIASES` and saving them never offers a rescore.
 *
 * The card exists so the pre-launch Vertex calibration can be applied as a settings change rather
 * than a deploy (`intent.md` FR-10). The values that ship are provisional: they were derived on
 * Snowflake Cortex embeddings and this pipeline runs Vertex, which scores on a different scale.
 */
export function SerpMatchScoringCard({
  value,
  onChange,
}: {
  value: SerpMatchConfig;
  onChange: (next: SerpMatchConfig) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">SERP Match</CardTitle>
        <p className="text-sm text-muted-foreground">
          How similar a page-1 result must be to count as “a product like ours”. Does not affect KPS.
        </p>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="serp-match-multiplier">Cutoff multiplier</Label>
          <Input
            id="serp-match-multiplier"
            type="number"
            step="0.01"
            min={0}
            max={1}
            value={value.cutoffMultiplier}
            onChange={(e) => onChange({ ...value, cutoffMultiplier: Number(e.target.value) })}
          />
          <p className="text-xs text-muted-foreground">
            Applied to the average similarity of the hero’s closest competitors. Lower means a
            stricter bar.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="serp-match-anchor">Competitors in the anchor</Label>
          <Input
            id="serp-match-anchor"
            type="number"
            step="1"
            min={1}
            max={100}
            value={value.anchorTopN}
            onChange={(e) => onChange({ ...value, anchorTopN: Number(e.target.value) })}
          />
          <p className="text-xs text-muted-foreground">
            How many of the most similar products the bar is averaged over.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
